// The brief interview, "Help me make one" (ADR 0027, docs/specs/campaigns-briefs.md section 6).

import type { ApiTransport } from "./client.ts";
import type { Uuid } from "./types.ts";

/** The question waiting on the left, with the model's example answers. */
export interface InterviewAsk {
  question: string;
  chips: string[];
  /** Several chips may be picked (Next and Skip under them). */
  multi: boolean;
  free_text: boolean;
}

export interface InterviewAnswered {
  question: string;
  answer: string;
}

/** Who a new sub-campaign is shared with, as the model suggests it. */
export type InterviewAudience = "owner" | "team" | "members";

export interface InterviewDetails {
  name: string;
  visibility?: InterviewAudience | null;
  tag_name?: string | null;
}

/** The interview's draft, stored on its conversation so a reload restores it. */
export interface InterviewDraft {
  summary: string;
  body_md: string;
  ask: InterviewAsk | null;
  answered: InterviewAnswered[];
  /** Questions asked so far, and the model's estimate of the whole ("3 of about 6"). */
  asked: number;
  about: number | null;
  details: InterviewDetails | null;
  /** The model said the brief is ready: Continue is enabled. */
  ready: boolean;
  /** The heading the model wrote last. */
  last_heading: string | null;
}

export interface BriefInterview {
  id: Uuid;
  parent_id: Uuid | null;
  parent_name: string | null;
  draft: InterviewDraft;
  created_at: string;
}

export type InterviewEvent =
  | { type: "writing"; summary: string | null; body_md: string }
  | { type: "working"; label: string }
  | { type: "draft"; draft: InterviewDraft }
  | { type: "budget"; remaining_usd: number; exhausted: boolean }
  | { type: "done"; draft: InterviewDraft }
  | { type: "error"; code: string; message: string };

export class BriefsApi {
  constructor(private readonly client: ApiTransport) {}

  /** 503 `assistant_unavailable` or 403 `ai_disabled`: open Create manually instead. */
  startInterview(parentId: Uuid | null) {
    return this.client.post<BriefInterview>("/v1/brief-interviews", { parent_id: parentId });
  }
  /** The caller's interview still open for this parent (null: a top-level campaign). */
  openInterview(parentId: Uuid | null, signal?: AbortSignal) {
    const q = parentId ? `?parent_id=${encodeURIComponent(parentId)}` : "";
    return this.client.get<{ interview: BriefInterview | null }>(
      `/v1/brief-interviews/open${q}`,
      signal,
    );
  }
  interview(id: Uuid, signal?: AbortSignal) {
    return this.client.get<BriefInterview>(`/v1/brief-interviews/${id}`, signal);
  }
  /** What the person typed into the draft. */
  saveDraft(id: Uuid, draft: { summary: string; body_md: string }) {
    return this.client.put<BriefInterview>(`/v1/brief-interviews/${id}/draft`, draft);
  }
  /** The person's answer (empty only to open the interview), streamed back as events. */
  answer(
    id: Uuid,
    text: string,
    draft?: { summary: string; body_md: string } | null,
    signal?: AbortSignal,
  ): AsyncGenerator<InterviewEvent> {
    return this.client.streamEvents<InterviewEvent>(
      `/v1/brief-interviews/${id}/messages`,
      { text, ...(draft ? { draft } : {}) },
      signal,
    );
  }
  /** The campaign was made from this draft (its first brief version reads "interview"). */
  end(id: Uuid, campaignId?: Uuid | null) {
    return this.client.post<void>(`/v1/brief-interviews/${id}/end`, {
      campaign_id: campaignId ?? null,
    });
  }
  /** Discard this draft. */
  discard(id: Uuid) {
    return this.client.del<void>(`/v1/brief-interviews/${id}`);
  }
}
